import { useCallback, useEffect, useRef, useState } from "react";

import { EVENTS_PAGE_SIZE } from "../constants/payoutBatches.constants";
import { PayoutBatchesService } from "../services/payoutBatches.service";
import type { PayoutBatchEvent } from "../types/payoutBatches.types";

export interface UsePayoutBatchEventsResult {
  events: PayoutBatchEvent[];
  total: number;
  isLoading: boolean;
  hasError: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

interface EventsState {
  batchId: string;
  events: PayoutBatchEvent[];
  total: number;
}

/**
 * The batch's append-only trail, newest first as the API serves it, read on demand (the drawer) and extended
 * page by page. `refresh` re-reads the first page — what the drawer does when the batch moves while open.
 */
export function usePayoutBatchEvents(batchId: string, enabled: boolean): UsePayoutBatchEventsResult {
  const [state, setState] = useState<EventsState | null>(null);
  const [settledFor, setSettledFor] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const requestSeq = useRef(0);

  const current = state?.batchId === batchId ? state : null;

  const readPage = useCallback(
    async (skip: number) => {
      const seq = ++requestSeq.current;
      try {
        const { data } = await PayoutBatchesService.listEvents(batchId, {
          skip,
          limit: EVENTS_PAGE_SIZE,
        });
        if (seq !== requestSeq.current) return;
        setState((prev) => {
          const same = prev?.batchId === batchId && skip > 0 ? prev.events : [];
          return {
            batchId,
            events: [...same, ...data.data],
            total: data.total,
          };
        });
        setHasError(false);
        setSettledFor(batchId);
      } catch {
        if (seq !== requestSeq.current) return;
        setHasError(true);
        setSettledFor(batchId);
      }
    },
    [batchId],
  );

  const readPageRef = useRef(readPage);
  useEffect(() => {
    readPageRef.current = readPage;
  });

  // Through the ref on purpose: the read settles asynchronously, so no render cascades from here.
  useEffect(() => {
    if (!enabled) return;
    void readPageRef.current(0);
  }, [enabled, batchId]);

  const loadMore = useCallback(
    () => readPage(current?.events.length ?? 0),
    [current?.events.length, readPage],
  );
  const refresh = useCallback(() => readPage(0), [readPage]);

  return {
    events: current?.events ?? [],
    total: current?.total ?? 0,
    isLoading: enabled && settledFor !== batchId,
    hasError,
    hasMore: (current?.events.length ?? 0) < (current?.total ?? 0),
    loadMore,
    refresh,
  };
}
